import { type ElementType } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, FileText, Building2, ShieldCheck, Upload, Users } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EstadoError } from '@/components/ui/estado-vacio';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useCarga } from '@/features/dynamic-pricing/monitoreo/hooks/use-carga';
import { atributosSoporteNoPuede, useEnSoporte } from '@/features/mini-prixia/soporte-solo-mira';
import { toast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { formatoPorcentaje } from '@/lib/utils/formato-precio';
import { diasQueQuedan } from '@/lib/utils/dias-que-quedan';
import { MarcoDeMiEmpresa } from '../components/marco-de-mi-empresa';
import { rutaDeSeccion } from '../lib/secciones';
import { miEmpresaService, type ResumenDeMiEmpresa } from '../services/mi-empresa-service';
import { useEmpresaStore, type Apariencia } from '../store/empresa-store';
import { useSubirLogo } from '../hooks/use-subir-logo';

/** Los cinco degradados de Apariencia, medidos en la captura de Melanie. */
const DEGRADADOS: Array<{ id: Apariencia; nombre: string; colores: [string, string, string] }> = [
  { id: 'prixia', nombre: 'Prixia', colores: ['#6623AB', '#972B72', '#D5502C'] },
  { id: 'azul', nombre: 'Azul', colores: ['#0E2357', '#1C3782', '#245BD8'] },
  { id: 'verde', nombre: 'Verde', colores: ['#065741', '#047454', '#0DAC79'] },
  { id: 'rojo', nombre: 'Rojo', colores: ['#8C1D1D', '#B51C1C', '#E83F3F'] },
  { id: 'negro', nombre: 'Negro', colores: ['#101010', '#232323', '#494949'] },
];

/** «Datec LTDA.» → «Datec»: el nombre sin la forma societaria. */
export const nombreCorto = (nombre: string | undefined) =>
  (nombre ?? '').replace(/(^|\s)(ltda|s\.\s?a|s\.r\.l|srl)\.?(?=\s|$)/gi, '$1').trim();

/** Mi empresa › Resumen (maqueta de Melanie, 25-oct). */
export function ResumenSection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const subscription = useAuthStore((s) => s.subscription);
  const empresa = useEmpresaStore((s) => s.empresa);
  const guardar = useEmpresaStore((s) => s.guardar);
  const cargar = useEmpresaStore((s) => s.cargar);
  const resumen = useCarga(() => miEmpresaService.resumen(), []);
  const { archivo, subiendo, subir: subirLogo } = useSubirLogo(() => resumen.recargar());
  const enSoporte = useEnSoporte();

  const elegirApariencia = async (id: Apariencia) => {
    try {
      await guardar({ uiTheme: id });
      if (user?.tenantId) await cargar(user.tenantId, true);
    } catch {
      toast({ title: 'No se pudo cambiar la apariencia', variant: 'destructive' });
    }
  };

  const r = resumen.datos;
  const color = empresa?.brandColor || undefined;
  const quedan = diasQueQuedan(subscription?.endDate);
  const diasRestantes = quedan === null ? null : Math.max(0, quedan);
  const elegida = empresa?.uiTheme ?? 'prixia';

  const irA = (seccion: string) => {
    if (seccion === 'resumen') archivo.current?.click();
    else navigate(rutaDeSeccion(seccion as never));
  };

  return (
    <MarcoDeMiEmpresa seccion="resumen">
      <input
        ref={archivo}
        type="file"
        accept="image/png,image/jpeg"
        className="sr-only"
        aria-label="Elegir el logo de la empresa"
        onChange={(e) => subirLogo(e.target.files?.[0])}
      />

      {resumen.estado === 'error' && !r ? (
        <EstadoError onReintentar={resumen.recargar}>No pudimos cargar el resumen.</EstadoError>
      ) : (
        <>
          <section
            aria-label="Tu empresa"
            className="overflow-hidden rounded-tarjeta border border-t-4 bg-card shadow-card"
            style={{ borderTopColor: color ?? 'hsl(var(--primary))' }}
          >
            <div className="grid gap-8 p-7 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:items-center">
              <div className="flex flex-wrap items-center gap-7">
                <div className="flex flex-col items-center gap-3">
                  <div
                    className={cn(
                      'grid h-24 w-[188px] place-items-center rounded-2xl border-2 border-dashed border-border p-3',
                      // El logo va sobre blanco también en oscuro, como en el PDF: sus letras suelen ser oscuras.
                      empresa?.logoUrl ? 'bg-white' : 'bg-lienzo'
                    )}
                  >
                    {empresa?.logoUrl ? (
                      <img
                        src={empresa.logoUrl}
                        alt={`Logo de ${empresa.name}`}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <Building2 className="size-8 text-muted-foreground" aria-hidden="true" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => archivo.current?.click()}
                    disabled={subiendo || enSoporte}
                    {...atributosSoporteNoPuede(enSoporte)}
                    className="inline-flex h-10 items-center gap-2 rounded-full border bg-campo px-4 text-[15px] font-semibold text-primary transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                  >
                    <Upload className="size-4" aria-hidden="true" />
                    {subiendo ? 'Subiendo…' : 'Cambiar logo'}
                  </button>
                </div>
                <div className="flex min-w-0 flex-col gap-2">
                  <h2 className="text-2xl font-semibold">
                    {empresa?.name ?? r?.empresa.nombre ?? '…'}
                  </h2>
                  <p className="text-[15px] text-muted-foreground">
                    NIT {empresa?.nit || r?.empresa.nit || '—'} · {empresa?.country || 'Bolivia'} ·{' '}
                    {r
                      ? `${r.usuarios.total} ${r.usuarios.total === 1 ? 'usuario' : 'usuarios'}`
                      : '…'}
                  </p>
                  {subscription ? (
                    <span className="inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                      {subscription.planTier.toUpperCase()}
                      {diasRestantes != null && ` · ${diasRestantes} días restantes`}
                    </span>
                  ) : (
                    <span className="inline-flex w-fit rounded-full bg-muted px-3 py-1 text-sm font-semibold text-muted-foreground">
                      Sin plan activo
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {!r ? (
                  <Skeleton className="h-24 w-full" />
                ) : (
                  <>
                    <p className="text-lg font-semibold">
                      Tu empresa está configurada al{' '}
                      {formatoPorcentaje(r.configurada, { decimales: 0 })}
                    </p>
                    <div
                      className="h-2.5 overflow-hidden rounded-full bg-muted"
                      role="progressbar"
                      aria-valuenow={r.configurada}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Configuración de la empresa"
                    >
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${r.configurada}%`, backgroundColor: color }}
                      />
                    </div>
                    {r.pendientes.length === 0 ? (
                      <p className="flex items-center gap-2 text-[15px] text-ok">
                        <Check className="size-4" aria-hidden="true" /> Todo configurado.
                      </p>
                    ) : (
                      <ul className="flex flex-col gap-1.5">
                        {r.pendientes.map((p) => (
                          <li key={p.clave} className="flex items-center gap-2 text-[15px]">
                            <span
                              className="size-1.5 shrink-0 rounded-full bg-prixia-gold"
                              aria-hidden="true"
                            />
                            <span>{p.texto} ·</span>
                            <button
                              type="button"
                              onClick={() => irA(p.seccion)}
                              // «Subir» (sección resumen) abre el logo y guarda: soporte no. Los demás sólo navegan.
                              {...atributosSoporteNoPuede(enSoporte && p.seccion === 'resumen')}
                              className="font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {p.accion}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>

          <section
            aria-labelledby="apariencia"
            className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-tarjeta border bg-card px-6 py-4 shadow-card"
          >
            <h2 id="apariencia" className="text-[15px] font-semibold">
              Apariencia
            </h2>
            <span className="text-[15px] text-muted-foreground">degradado de la plataforma</span>
            <div
              role="radiogroup"
              aria-labelledby="apariencia"
              className="flex items-center gap-2.5"
            >
              {DEGRADADOS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={elegida === d.id}
                  aria-label={d.nombre}
                  title={d.nombre}
                  onClick={() => elegirApariencia(d.id)}
                  {...atributosSoporteNoPuede(enSoporte)}
                  className={cn(
                    'size-9 rounded-full ring-offset-2 ring-offset-background transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    elegida === d.id && 'ring-2 ring-foreground'
                  )}
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${d.colores[0]}, ${d.colores[1]} 50%, ${d.colores[2]})`,
                  }}
                />
              ))}
            </div>
            <span className="ml-auto text-sm text-muted-foreground">Por defecto: Prixia</span>
          </section>

          <TarjetasDeResumen resumen={r} />
        </>
      )}
    </MarcoDeMiEmpresa>
  );
}

function TarjetasDeResumen({ resumen: r }: { resumen: ResumenDeMiEmpresa | null }) {
  const navigate = useNavigate();
  const empresa = useEmpresaStore((s) => s.empresa);
  if (!r) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-44 rounded-tarjeta" />
        ))}
      </div>
    );
  }
  const tarjetas: Array<{
    icono: ElementType;
    titulo: string;
    valor: string;
    detalle: string;
    seccion: Parameters<typeof rutaDeSeccion>[0];
  }> = [
    {
      icono: Users,
      titulo: 'Usuarios',
      valor: String(r.usuarios.total),
      detalle: `${r.usuarios.activos} ${r.usuarios.activos === 1 ? 'activo' : 'activos'}${
        r.usuarios.invitaciones
          ? ` · ${r.usuarios.invitaciones} ${r.usuarios.invitaciones === 1 ? 'invitación pendiente' : 'invitaciones pendientes'}`
          : ''
      }`,
      seccion: 'usuarios',
    },
    {
      icono: ShieldCheck,
      titulo: 'Roles',
      valor: String(r.roles.total),
      detalle: r.roles.nombres.join(', ') || 'Todavía sin roles',
      seccion: 'roles',
    },
    {
      icono: Building2,
      titulo: 'Equipos',
      valor: String(r.equipos.total),
      detalle: r.equipos.resumen,
      seccion: 'equipos',
    },
    {
      icono: Check,
      titulo: 'Aprobaciones',
      valor: `${r.aprobaciones.reglas} ${r.aprobaciones.reglas === 1 ? 'regla' : 'reglas'}`,
      detalle: r.aprobaciones.tipos.length
        ? r.aprobaciones.tipos.join(', ').replace(/, ([^,]*)$/, ' y $1')
        : 'Sin reglas todavía',
      seccion: 'aprobaciones',
    },
    {
      icono: FileText,
      titulo: 'Documentos',
      valor: nombreCorto(empresa?.name) || 'Plantilla',
      detalle: `plantilla de cotización · ${r.documentos.revisada ? 'lista' : 'por revisar'}`,
      seccion: 'documentos',
    },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {tarjetas.map((t) => (
        <button
          key={t.titulo}
          type="button"
          onClick={() => navigate(rutaDeSeccion(t.seccion))}
          className="flex flex-col gap-3 rounded-tarjeta border bg-card p-6 text-left shadow-card transition-shadow hover:shadow-overlay focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="flex items-center gap-3">
            <span className="grid size-11 place-items-center rounded-xl bg-accent text-primary">
              <t.icono className="size-5" aria-hidden="true" />
            </span>
            <span className="text-[17px] font-semibold">{t.titulo}</span>
          </span>
          <span
            title={t.valor}
            className={cn(
              // `pb-1`: con `truncate` el renglón recorta la cola de la «g» de «reglas».
              'pb-1 font-titulo font-semibold',
              // Un nombre largo («Farmacia San Jorge») baja a dos renglones en vez de cortarse.
              // El interlineado va después del tamaño: `cn` descarta el que va antes.
              t.valor.length > 12
                ? 'line-clamp-2 text-[22px] leading-[1.3]'
                : 'truncate text-[28px] leading-[1.3]'
            )}
          >
            {t.valor}
          </span>
          <span className="text-sm text-muted-foreground">{t.detalle}</span>
        </button>
      ))}
    </div>
  );
}
